import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { LayoutGrid, History, Plus, LogOut, Activity, Radar } from "lucide-react";

export function Logo({ className = "" }) {
  return (
    <Link to="/" className={`flex items-center gap-2 ${className}`} data-testid="logo-link">
      <div className="w-7 h-7 rounded-md bg-slate-900 flex items-center justify-center">
        <Activity className="w-4 h-4 text-white" strokeWidth={2.5} />
      </div>
      <span className="font-display font-bold text-lg tracking-tight text-slate-900">Decision Analog</span>
    </Link>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutGrid },
    { to: "/intel", label: "Intelligence", icon: Radar },
    { to: "/history", label: "History", icon: History },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl backdrop-saturate-150 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo />
            <nav className="hidden md:flex items-center gap-1">
              {nav.map((n) => {
                const active = location.pathname.startsWith(n.to);
                return (
                  <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:text-slate-900 hover:bg-slate-50"}`}>
                    <n.icon className="w-4 h-4" /> {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={() => navigate("/decisions/new")} data-testid="nav-analyze-button"
              className="bg-slate-900 hover:bg-slate-800 rounded-lg gap-1.5">
              <Plus className="w-4 h-4" /> Analyze a Decision
            </Button>
            <div className="hidden sm:flex items-center gap-2 pl-3 border-l border-slate-200">
              <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-700">
                {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
              </div>
              <button onClick={logout} data-testid="logout-button" className="text-slate-400 hover:text-rose-600 transition-colors">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8">{children}</main>
    </div>
  );
}
